import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  FolderOpen, Plus, Loader2, Trash2, Eye, Download, Upload,
  FileText, Image, AlertTriangle, Calendar
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { DatePickerFr } from "../../ui/date-picker-fr";
import {
  DOCUMENT_CATEGORY_OPTIONS,
  DOCUMENT_CATEGORY_LABELS,
  formatDate,
} from "../../../lib/fleet-constants";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function DocumentsTab({ vehicle, onUpdate }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, document: null });
  
  const [form, setForm] = useState({
    category: "other",
    custom_label: "",
    expiry_date: "",
    file: null
  });
  
  const token = localStorage.getItem("token");

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/fleet/vehicles/${vehicle.id}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error("Error fetching documents:", err);
      toast.error("Erreur lors du chargement des documents");
    } finally {
      setLoading(false);
    }
  }, [vehicle.id, token]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm(prev => ({ ...prev, file }));
    }
  };

  const resetForm = () => {
    setForm({
      category: "other",
      custom_label: "",
      expiry_date: "",
      file: null
    });
  };

  const handleUpload = async () => {
    if (!form.file) {
      toast.error("Veuillez sélectionner un fichier");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", form.file);
      formData.append("category", form.category);
      if (form.custom_label) formData.append("custom_label", form.custom_label);
      if (form.expiry_date) formData.append("expiry_date", form.expiry_date);
      
      await axios.post(
        `${API}/fleet/vehicles/${vehicle.id}/documents`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      toast.success("Document uploadé");
      setShowUploadModal(false);
      resetForm();
      fetchDocuments();
    } catch (err) {
      console.error("Error uploading document:", err);
      toast.error("Erreur", {
        description: err.response?.data?.detail || "Impossible d'uploader le document"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.document) return;
    
    try {
      await axios.delete(
        `${API}/fleet/vehicles/${vehicle.id}/documents/${deleteDialog.document.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Document supprimé");
      setDeleteDialog({ open: false, document: null });
      fetchDocuments();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith("image/")) {
      return <Image className="w-8 h-8 text-blue-500" />;
    }
    return <FileText className="w-8 h-8 text-red-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntil = (expiry - today) / (1000 * 60 * 60 * 24);
    return daysUntil <= 60 && daysUntil >= 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  // Group documents by category
  const groupedDocuments = documents.reduce((acc, doc) => {
    const cat = doc.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{documents.length} document(s)</p>
        <Button onClick={() => setShowUploadModal(true)} data-testid="upload-document-btn">
          <Upload className="w-4 h-4 mr-2" />
          Ajouter un document
        </Button>
      </div>
      
      {/* Documents Grid */}
      {documents.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun document</h3>
            <p className="text-slate-500 mb-4">
              Ajoutez des documents liés à ce véhicule (carte grise, assurance, etc.)
            </p>
            <Button onClick={() => setShowUploadModal(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Ajouter un document
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedDocuments).map(([category, docs]) => (
            <div key={category}>
              <h3 className="text-sm font-medium text-slate-700 mb-3">
                {DOCUMENT_CATEGORY_LABELS[category] || category}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {docs.map((doc) => (
                  <Card 
                    key={doc.id}
                    className={`hover:shadow-md transition-shadow ${
                      isExpired(doc.expiry_date) ? "border-red-200 bg-red-50" :
                      isExpiringSoon(doc.expiry_date) ? "border-orange-200 bg-orange-50" : ""
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {getFileIcon(doc.mime_type)}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate" title={doc.custom_label || doc.original_filename}>
                            {doc.custom_label || doc.original_filename}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {doc.original_filename}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            {formatFileSize(doc.file_size)} • {formatDate(doc.created_at)}
                          </p>
                          
                          {doc.expiry_date && (
                            <div className="flex items-center gap-1 mt-2">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              <span className={`text-xs ${
                                isExpired(doc.expiry_date) ? "text-red-600 font-medium" :
                                isExpiringSoon(doc.expiry_date) ? "text-orange-600 font-medium" :
                                "text-slate-500"
                              }`}>
                                {isExpired(doc.expiry_date) ? "Expiré le" : "Expire le"} {formatDate(doc.expiry_date)}
                              </span>
                              {(isExpired(doc.expiry_date) || isExpiringSoon(doc.expiry_date)) && (
                                <AlertTriangle className="w-3 h-3 text-orange-500" />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`${API}/fleet/files/${doc.file_id}/view`, "_blank")}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteDialog({ open: true, document: doc })}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un document</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Catégorie *</Label>
              <Select value={form.category} onValueChange={(v) => handleChange("category", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_CATEGORY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Libellé personnalisé</Label>
              <Input
                value={form.custom_label}
                onChange={(e) => handleChange("custom_label", e.target.value)}
                placeholder="Ex: Carte grise véhicule de fonction"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Date d'expiration (si applicable)</Label>
              <DatePickerFr
                value={form.expiry_date}
                onChange={(v) => handleChange("expiry_date", v)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Fichier *</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                {form.file ? (
                  <div className="flex items-center justify-center gap-3">
                    {getFileIcon(form.file.type)}
                    <div className="text-left">
                      <p className="font-medium text-sm">{form.file.name}</p>
                      <p className="text-xs text-slate-500">{formatFileSize(form.file.size)}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setForm(prev => ({ ...prev, file: null }))}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
                      onChange={handleFileSelect}
                    />
                    <div className="flex flex-col items-center">
                      <Upload className="w-8 h-8 text-slate-300 mb-2" />
                      <p className="text-sm text-slate-500">
                        Cliquez pour sélectionner un fichier
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        PDF, JPG, PNG (max 25 Mo)
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)} disabled={uploading}>
              Annuler
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !form.file}>
              {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Uploader
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, document: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce document ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le document "{deleteDialog.document?.custom_label || deleteDialog.document?.original_filename}" sera supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
